import compact from 'lodash/compact'
import includes from 'lodash/includes'
import noop from 'lodash/noop'
import orderBy from 'lodash/orderBy'
import split from 'lodash/split'
import NextImage, { ImageProps } from 'next/image'
import NextLegacyImage, { ImageLoaderProps as ImageLegacyLoaderProps, ImageProps as ImageLegacyProps } from 'next/legacy/image'
import React, { CSSProperties, FC, useMemo } from 'react'

import { ImagePins } from '@Components/Image/Pins'
import { Image as ImageData, ImagePin } from '@Globals/Image'

import styles from './Image.module.scss'

interface ContentstackLoaderProps extends Omit<ImageLegacyLoaderProps, 'quality'> {
  cropRatio?: string
  objectFit?: ImageLegacyProps['objectFit']
  objectPosition?: ImageLegacyProps['objectPosition']
  quality?: number | string
}

export const contentstackLoader = (
  {
    cropRatio,
    objectFit,
    objectPosition = 'center top',
    quality = '75,90',
    src,
    width,
  }: ContentstackLoaderProps,
) => {
  if (!includes(src, 'contentstack')) {
    return src
  }

  const url = new URL(src)

  url.searchParams.set('format', 'pjpg')
  url.searchParams.set('auto', 'webp')

  if (quality) {
    url.searchParams.set('quality', quality.toString())
  }

  if (width) {
    url.searchParams.set('width', width.toString())
  }

  if ((objectFit === 'cover' || objectFit === 'contain') && cropRatio) {
    const offset = {
      bottom: '100',
      center: '50',
      left: '0',
      right: '100',
      top: '0',
    }

    const [offsetX, offsetY] = split(objectPosition.toString(), ' ')

    const cropParam = `${cropRatio},offset-x${offset[offsetX]},offset-y${offset[offsetY]}`

    url.searchParams.set('crop', cropParam)
  }

  return url.toString()
}

const generateSrcSet = ({
  width,
  ...props
}: ContentstackLoaderProps) => [
  contentstackLoader({ width, ...props }),
  ', ',
  contentstackLoader({ width: width * 2, ...props }),
  ' 2x',
].join('')

export const generateProductLinkingDataImage = (src: string): string => contentstackLoader({
  src,
  width: 250,
})

interface ImageV2Props extends ImageLegacyProps {
  cropRatio?: string
}

export const ImageV2: FC<ImageV2Props> = (props) => {
  const {
    cropRatio,
    objectFit,
    objectPosition,
    src,
    ...rest
  } = props

  if (!src) {
    console.error('invalid src for ImageV2')
    return null
  }

  return (
    <NextLegacyImage
      loader={params => contentstackLoader({
        ...params,
        cropRatio,
        objectFit,
        objectPosition,
      })}
      objectFit={objectFit}
      src={src}
      {...rest}
    />
  )
}

interface ImageV3Props extends ImageProps {
  className?: string
  cropRatio?: string
  overrideQuality?: number | string
}

export const ImageV3: FC<ImageV3Props> = ({
  className,
  cropRatio,
  overrideQuality,
  src,
  style,
  ...rest
}) => {
  if (!src) {
    console.error('invalid src for ImageV3')
    return null
  }

  return (
    <NextImage
      className={className}
      loader={
        params => contentstackLoader({
          ...params,
          cropRatio,
          objectFit: style?.objectFit,
          objectPosition: style?.objectPosition,
          quality: overrideQuality || params.quality,
        })
      }
      src={src}
      style={style}
      {...rest}
    />
  )
}

type ImageV4Props = Omit<React.JSX.IntrinsicElements['img'], 'src' | 'width'> & {
  quality?: number
  src: string
  width: number
}

export const ImageV4: FC<ImageV4Props> = ({
  alt,
  onLoad = noop,
  quality,
  src,
  style,
  width,
  ...rest
}) => (
  <img
    alt={alt}
    onLoad={onLoad}
    src={contentstackLoader({
      quality,
      src,
      width: width * 2,
      objectFit: style?.objectFit,
      objectPosition: style?.objectPosition,
    })}
    srcSet={generateSrcSet({
      quality,
      src,
      width,
      objectFit: style?.objectFit,
      objectPosition: style?.objectPosition,
    })}
    style={style}
    {...rest}
  />
)

type IMGStyleProps = NonNullable<React.JSX.IntrinsicElements['img']['style']>

/** number is converted to pixels (px), and string is parsed as viewport width (vw) */
type PixelsOrViewportWidth = number | string

type Breakpoint = [
  /** min-width breakpoint */
  breakpoint: number,
  /** max container width before next breakpoint */
  width: PixelsOrViewportWidth,
  /** max container height */
  height?: PixelsOrViewportWidth,
]

export type PictureV2Props = Omit<React.JSX.IntrinsicElements['img'], 'priority'> & {
  breakpoints: Breakpoint[]
  /** Only applies to desktop */
  cropOnMobile?: boolean
  cropRatio?: string
  desktop: ImageData
  mobile?: ImageData
  objectFit?: IMGStyleProps['objectFit']
  objectPosition?: IMGStyleProps['objectPosition']
  priority?: boolean
}

const calculateBreakpointWidth = (breakpoint: number, width: PixelsOrViewportWidth): number => {
  if (typeof width === 'number') {
    return width
  }

  return (breakpoint / 100) * parseInt(width, 10)
}

export const PictureV2: FC<PictureV2Props> = ({
  alt = '',
  breakpoints,
  cropOnMobile,
  cropRatio,
  desktop,
  mobile,
  objectFit,
  objectPosition,
  priority = false,
  ...imgProps
}) => {
  const breakpointsSorted = useMemo(() => orderBy(breakpoints, [0], ['desc']), [breakpoints])

  const sources = useMemo(() => compact(breakpointsSorted.map(([breakpoint], index) => {
    const next = breakpointsSorted[index - 1]
    if (!next) {
      return undefined
    }
    const isDesktop = breakpoint >= 768
    const [nextBreakpoint, nextWidth] = next
    const width = calculateBreakpointWidth(nextBreakpoint, nextWidth)
    let src = desktop.url
    if (!isDesktop && mobile) {
      src = mobile.url
    }
    const srcSet = generateSrcSet({
      cropRatio: isDesktop || cropOnMobile ? cropRatio : undefined,
      objectFit,
      objectPosition,
      src,
      width,
    })
    return ({ breakpoint, srcSet })
  })), [breakpointsSorted, cropOnMobile, cropRatio, desktop.url, mobile, objectFit, objectPosition])

  return (
    <span
      className={styles.image}
      style={{
        '--desktopHeight': `${desktop.dimensions.height}`,
        '--desktopWidth': `${desktop.dimensions.width}`,
        '--mobileHeight': mobile?.dimensions.height ? `${mobile?.dimensions.height}` : `${desktop.dimensions.height}`,
        '--mobileWidth': mobile?.dimensions.width ? `${mobile?.dimensions.width}` : `${desktop.dimensions.width}`,
      } as CSSProperties}
    >
      <span className={styles.padder} />
      <picture>
        {sources.map(({
          breakpoint,
          srcSet,
        }) => (
          <source
            key={breakpoint}
            media={`(min-width: ${breakpoint}px)`}
            srcSet={srcSet}
          />
        ))}
        <img
          alt={alt}
          fetchPriority={priority ? 'high' : 'auto'}
          srcSet={generateSrcSet({
            src: mobile?.url || desktop.url,
            width: 320,
          })}
          style={{
            objectFit,
            objectPosition,
          } as CSSProperties}
          {...imgProps}
        />
      </picture>
      <ImagePins desktopPins={desktop?.pins} mobilePins={mobile?.pins} />
    </span>
  )
}

function calculateCenterStyle(image: ImageData): React.CSSProperties {
  if (image.dimensions.width === 0 || image.dimensions.height === 0) {
    return {
      height: 'auto',
      width: 'auto',
    }
  }

  if (image.dimensions.width === image.dimensions.height) {
    return {
      aspectRatio: 1,
      minHeight: '100%',
      minWidth: '100%',
    }
  }

  const aspectRatio = image.dimensions.width / image.dimensions.height
  const height = aspectRatio > 1 ? '100%' : 'auto'
  const width = aspectRatio < 1 ? '100%' : 'auto'
  const minHeight = aspectRatio < 1 ? '100%' : undefined
  const minWidth = aspectRatio > 1 ? '100%' : undefined

  return {
    aspectRatio,
    height,
    minHeight,
    minWidth,
    width,
  }
}

export const ImageWithPins = ({
  image,
  loading,
  pins,
  priority,
  sizes,
}: {
  image: ImageData
  loading?: ImageProps['loading']
  pins?: ImagePin[] | null
  priority?: ImageProps['priority']
  sizes?: string
}) => {
  const items = pins || image?.pins || []

  return (
    <div className={styles.imageWithPins}>
      <div className={styles.center} style={calculateCenterStyle(image)}>
        <ImageV3
          alt=""
          height={image?.dimensions?.height || 0}
          loading={loading}
          priority={priority}
          sizes={sizes}
          src={image?.url || ''}
          width={image?.dimensions?.width || 0}
        />
        {items && items.length !== 0 && <ImagePins pins={items} />}
      </div>
    </div>
  )
}
